import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShieldCheckIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { smsAPI } from '../../../services/api';

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedRole, setSelectedRole] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);

  const [formData, setFormData] = useState({
    role_name: '',
    role_code: '',
    description: '',
    permission_ids: [],
  });

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await smsAPI.getRoles();
      setRoles(response.data || []);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      setRoles([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await smsAPI.getPermissionsGrouped();
      setPermissions(response.data || []);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setPermissions([]); // Set empty array on error
    }
  };

  const handleCreateRole = () => {
    setModalMode('create');
    setFormData({
      role_name: '',
      role_code: '',
      description: '',
      permission_ids: [],
    });
    setShowModal(true);
  };

  const handleEditRole = async (role) => {
    try {
      const response = await smsAPI.getRoleById(role.id);
      const roleDetails = response.data;
      setModalMode('edit');
      setSelectedRole(roleDetails);
      setFormData({
        role_name: roleDetails.role_name,
        role_code: roleDetails.role_code,
        description: roleDetails.description,
        permission_ids: roleDetails.permissions?.map(p => p.id) || [],
      });
      setShowModal(true);
    } catch (error) {
      console.error('Failed to fetch role details:', error);
    }
  };

  const handleDeleteRole = (role) => {
    setRoleToDelete(role);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await smsAPI.deleteRole(roleToDelete.id);
      setShowDeleteConfirm(false);
      setRoleToDelete(null);
      fetchRoles();
    } catch (error) {
      console.error('Failed to delete role:', error);
      alert(error.response?.data?.error || 'Failed to delete role');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalMode === 'create') {
        await smsAPI.createRole(formData);
      } else {
        await smsAPI.updateRole(selectedRole.id, formData);
      }
      setShowModal(false);
      fetchRoles();
    } catch (error) {
      console.error('Failed to save role:', error);
      alert(error.response?.data?.error || 'Failed to save role');
    }
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permission_ids: prev.permission_ids.includes(permissionId)
        ? prev.permission_ids.filter(id => id !== permissionId)
        : [...prev.permission_ids, permissionId]
    }));
  };

  const handleModuleToggle = (modulePermissions) => {
    const modulePermissionIds = modulePermissions.map(p => p.id);
    const allSelected = modulePermissionIds.every(id => 
      formData.permission_ids.includes(id)
    );

    setFormData(prev => ({
      ...prev,
      permission_ids: allSelected
        ? prev.permission_ids.filter(id => !modulePermissionIds.includes(id))
        : [...new Set([...prev.permission_ids, ...modulePermissionIds])]
    }));
  };

  const generateRoleCode = (name) => {
    return name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4F46E5]"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">Role Management</h1>
          <p className="text-sm text-[#6B7280] mt-1">
            Manage roles and assign permissions
          </p>
        </div>
        <button
          onClick={handleCreateRole}
          className="flex items-center gap-2 px-4 py-2 bg-[#4F46E5] text-white rounded-lg hover:bg-[#4338CA] transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          Create Role
        </button>
      </div>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.map((role) => (
          <motion.div
            key={role.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg border border-[#E5E7EB] p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#EEF2FF] rounded-lg">
                  <ShieldCheckIcon className="w-6 h-6 text-[#4F46E5]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#111827]">{role.role_name}</h3>
                  <p className="text-xs text-[#6B7280]">{role.role_code}</p>
                </div>
              </div>
              {role.is_system_role && (
                <span className="px-2 py-1 text-xs font-medium bg-[#FEF3C7] text-[#92400E] rounded">
                  System
                </span>
              )}
            </div>

            <p className="text-sm text-[#6B7280] mb-4 line-clamp-2">
              {role.description}
            </p>

            <div className="flex items-center justify-between mb-4">
              <span className="text-xs text-[#6B7280]">
                {role.user_count || 0} users
              </span>
              <span className={`px-2 py-1 text-xs font-medium rounded ${
                role.is_active
                  ? 'bg-[#D1FAE5] text-[#065F46]'
                  : 'bg-[#FEE2E2] text-[#991B1B]'
              }`}>
                {role.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => handleEditRole(role)}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-[#4F46E5] bg-[#EEF2FF] rounded-lg hover:bg-[#E0E7FF] transition-colors"
              >
                <PencilIcon className="w-4 h-4" />
                Edit
              </button>
              {!role.is_system_role && (
                <button
                  onClick={() => handleDeleteRole(role)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-[#DC2626] bg-[#FEE2E2] rounded-lg hover:bg-[#FECACA] transition-colors"
                >
                  <TrashIcon className="w-4 h-4" />
                  Delete
                </button>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-[#E5E7EB]">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-[#111827]">
                    {modalMode === 'create' ? 'Create New Role' : 'Edit Role'}
                  </h2>
                  <button
                    onClick={() => setShowModal(false)}
                    className="p-2 hover:bg-[#F3F4F6] rounded-lg transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5 text-[#6B7280]" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
                <div className="p-6 space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">
                        Role Name *
                      </label>
                      <input
                        type="text"
                        value={formData.role_name}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            role_name: e.target.value,
                            role_code: modalMode === 'create' ? generateRoleCode(e.target.value) : formData.role_code
                          });
                        }}
                        className="w-full px-3 py-2 border border-[#D1D5DB] rounded-lg focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent"
                        placeholder="e.g., Support Agent"
                        required
                        disabled={selectedRole?.is_system_role}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-2">
                        Role Code *
                      </label>
                      <input
                        type="text"
                        value={formData.role_code}
                        onChange={(e) => setFormData({ ...formData, role_code: e.target.value })}
                        className="w-full px-3 py-2 border border-[#D1D5DB] rounded-lg focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent"
                        placeholder="e.g., support_agent"
                        required
                        disabled={selectedRole?.is_system_role}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-[#D1D5DB] rounded-lg focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent"
                      rows="3"
                      placeholder="Describe the role's purpose and responsibilities"
                    />
                  </div>

                  {/* Permissions */}
                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-3">
                      Permissions ({formData.permission_ids.length} selected)
                    </label>
                    <div className="space-y-3 max-h-96 overflow-y-auto border border-[#E5E7EB] rounded-lg p-4">
                      {permissions.map((group) => (
                        <div key={group.module} className="border-b border-[#E5E7EB] pb-3 last:border-0">
                          <div className="flex items-center justify-between mb-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={group.permissions.every(p => formData.permission_ids.includes(p.id))}
                                onChange={() => handleModuleToggle(group.permissions)}
                                className="w-4 h-4 text-[#4F46E5] border-[#D1D5DB] rounded focus:ring-[#4F46E5]"
                              />
                              <span className="font-medium text-[#111827] capitalize">
                                {group.module}
                              </span>
                            </label>
                            <span className="text-xs text-[#6B7280]">
                              {group.permissions.filter(p => formData.permission_ids.includes(p.id)).length}/{group.permissions.length}
                            </span>
                          </div>
                          <div className="ml-6 space-y-2">
                            {group.permissions.map((permission) => (
                              <label key={permission.id} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={formData.permission_ids.includes(permission.id)}
                                  onChange={() => handlePermissionToggle(permission.id)}
                                  className="w-4 h-4 text-[#4F46E5] border-[#D1D5DB] rounded focus:ring-[#4F46E5]"
                                />
                                <span className="text-sm text-[#374151]">
                                  {permission.permission_name}
                                </span>
                                <span className="text-xs text-[#6B7280]">
                                  ({permission.action})
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="p-6 border-t border-[#E5E7EB] flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#4F46E5] text-white rounded-lg hover:bg-[#4338CA] transition-colors flex items-center gap-2"
                  >
                    <CheckIcon className="w-5 h-5" />
                    {modalMode === 'create' ? 'Create Role' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDeleteConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-[#FEE2E2] rounded-full">
                  <ExclamationTriangleIcon className="w-6 h-6 text-[#DC2626]" />
                </div>
                <h3 className="text-lg font-semibold text-[#111827]">Delete Role</h3>
              </div>

              <p className="text-[#6B7280] mb-6">
                Are you sure you want to delete the role "<strong>{roleToDelete?.role_name}</strong>"? 
                This action cannot be undone.
              </p>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-[#DC2626] text-white rounded-lg hover:bg-[#B91C1C] transition-colors"
                >
                  Delete Role
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RoleManagement;
